/**
 * Pipelines · Informes (Mis Informes) — Módulo HubSpot-style.
 *
 * Lista de informes guardados con búsqueda, tabs (Todos/Personalizados/Favoritos),
 * acciones por fila (editar/clonar/renombrar/eliminar/favorito) y botón "Crear informe"
 * que abre selector de plantillas o builder en blanco.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import {
  FileText, Plus, Search as SearchIcon, Star, MoreVertical, Edit3, Copy, Trash2,
  Type as TypeIcon, Download, X, Loader2, Database, BarChart3, TrendingUp, Hash,
  Filter as FilterIcon, ChevronLeft, LayoutDashboard, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";
import Sidebar from "../../components/Sidebar";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_ICONS = { BarChart3, TrendingUp, Hash, Filter: FilterIcon };

export default function InformesPage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all"); // all | mine | favorites
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // informe being edited via builder
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (search) qs.set("q", search);
      if (tab === "favorites") qs.set("only_favorites", "true");
      if (tab === "mine") qs.set("only_mine", "true");
      const r = await fetch(`${API_URL}/api/pipelines/informes?${qs}`, getAuthHeaders());
      const d = await r.json();
      setItems(d.items || []);
    } catch { toast.error("Error al cargar informes"); }
    setLoading(false);
  }, [search, tab, getAuthHeaders]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // close popover on outside click
  useEffect(() => {
    const h = () => setOpenMenuId(null);
    if (openMenuId) {
      document.addEventListener("click", h);
      return () => document.removeEventListener("click", h);
    }
  }, [openMenuId]);

  const handleToggleFav = async (id) => {
    try {
      await fetch(`${API_URL}/api/pipelines/informes/${id}/favorite`, { method: "PATCH", ...getAuthHeaders() });
      fetchItems();
    } catch { toast.error("Error al marcar favorito"); }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`¿Eliminar el informe "${it.name}"?`)) return;
    try {
      await fetch(`${API_URL}/api/pipelines/informes/${it.id}`, { method: "DELETE", ...getAuthHeaders() });
      toast.success("Informe eliminado");
      fetchItems();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleClone = async (it) => {
    try {
      await fetch(`${API_URL}/api/pipelines/informes/${it.id}/clone`, { method: "POST", ...getAuthHeaders() });
      toast.success("Informe clonado");
      fetchItems();
    } catch { toast.error("Error al clonar"); }
  };

  const handleRename = async (id) => {
    if (!renameValue.trim()) { setRenamingId(null); return; }
    try {
      await fetch(`${API_URL}/api/pipelines/informes/${id}/rename`, {
        method: "PATCH",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ name: renameValue.trim() }),
      });
      toast.success("Renombrado");
      setRenamingId(null);
      fetchItems();
    } catch { toast.error("Error al renombrar"); }
  };

  const handleExport = async (it) => {
    try {
      const r = await fetch(`${API_URL}/api/pipelines/informes/${it.id}/export`, getAuthHeaders());
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(it.name || "informe").replace(/\s+/g, "_")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exportado");
    } catch { toast.error("Error al exportar"); }
  };

  const tabCounts = useMemo(() => {
    return {
      all: items.length,
      mine: items.length, // backend filtra, así que cuando tab=mine, items ya viene filtrado
      favorites: items.filter((i) => i.is_favorite).length,
    };
  }, [items]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-64">
        <div className="max-w-[1500px] mx-auto p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <button onClick={() => navigate("/pipelines")} className="text-xs text-slate-500 hover:text-[#193966] flex items-center gap-1 mb-1" data-testid="back-to-pipelines">
                <ChevronLeft className="w-3 h-3" /> Volver a Pipelines
              </button>
              <h1 className="text-2xl font-bold text-[#193966] flex items-center gap-2">
                <FileText className="w-6 h-6 text-[#80BC00]" />
                Informes Estadísticos
              </h1>
              <p className="text-xs text-slate-500">Informes guardados sobre los pipelines · {items.length} {items.length === 1 ? "informe" : "informes"}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => navigate("/pipelines/paneles")} className="h-9" data-testid="go-paneles">
                <LayoutDashboard className="w-4 h-4 mr-1.5" /> Paneles
              </Button>
              <Button size="sm" onClick={() => setShowCreate(true)} className="h-9 bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="create-informe-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Crear informe
              </Button>
            </div>
          </div>

          {/* Tabs + Search */}
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
            <div className="border-b border-slate-200 px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-1" data-testid="informes-tabs">
                {[
                  { id: "all", label: "Todos los informes" },
                  { id: "mine", label: "Personalizados" },
                  { id: "favorites", label: "Favoritos" },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${tab === t.id ? "bg-[#193966] text-white font-semibold" : "text-slate-600 hover:bg-slate-100"}`}
                    data-testid={`tab-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="relative">
                <SearchIcon className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                <Input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nombre…"
                  className="h-8 w-64 text-xs pl-7"
                  data-testid="informes-search"
                />
              </div>
            </div>

            {/* Tabla */}
            {loading ? (
              <div className="py-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
            ) : items.length === 0 ? (
              <div className="py-16 flex flex-col items-center justify-center text-center px-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mb-3">
                  <FileText className="w-7 h-7 text-slate-400" />
                </div>
                <p className="text-sm font-semibold text-slate-700">Aún no tenés informes</p>
                <p className="text-[11px] text-slate-500 mt-1 max-w-[360px]">
                  Creá tu primer informe a partir de una plantilla o de cero seleccionando un pipeline.
                </p>
                <Button size="sm" onClick={() => setShowCreate(true)} className="mt-3 bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="empty-create-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Crear informe
                </Button>
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="informes-table">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2 text-left">Nombre</th>
                    <th className="px-3 py-2 text-left">Pipeline</th>
                    <th className="px-3 py-2 text-left">Propietario</th>
                    <th className="px-3 py-2 text-left">Última actualización</th>
                    <th className="px-3 py-2 text-left">Etiquetas</th>
                    <th className="px-3 py-2 text-right">Visitas</th>
                    <th className="px-3 py-2 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-b hover:bg-slate-50 group" data-testid={`informe-row-${it.id}`}>
                      <td className="px-3 py-2">
                        <button onClick={() => handleToggleFav(it.id)} title={it.is_favorite ? "Quitar de favoritos" : "Marcar como favorito"} data-testid={`fav-${it.id}`}>
                          <Star className={`w-4 h-4 ${it.is_favorite ? "text-yellow-400 fill-yellow-400" : "text-slate-300 hover:text-yellow-400"}`} />
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        {renamingId === it.id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="text"
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") handleRename(it.id); if (e.key === "Escape") setRenamingId(null); }}
                              className="h-7 text-sm"
                              autoFocus
                            />
                            <Button size="sm" onClick={() => handleRename(it.id)} className="h-7 px-2 bg-[#193966] text-white">OK</Button>
                          </div>
                        ) : (
                          <button onClick={() => setEditing(it)} className="text-sm font-semibold text-[#193966] hover:underline text-left" data-testid={`open-informe-${it.id}`}>
                            {it.name}
                          </button>
                        )}
                        {it.description && <p className="text-[11px] text-slate-500 italic mt-0.5">{it.description}</p>}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{it.pipeline_name || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{it.owner_name || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                        {it.updated_at ? new Date(it.updated_at).toLocaleString("es-DO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(it.tags || []).slice(0, 3).map((t) => (
                            <Badge key={t} variant="outline" className="text-[10px] py-0">{t}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 text-right tabular-nums">{it.view_count || 0}</td>
                      <td className="px-3 py-2 relative">
                        <button onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === it.id ? null : it.id); }} className="text-slate-400 hover:text-slate-700 opacity-0 group-hover:opacity-100" data-testid={`menu-btn-${it.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        {openMenuId === it.id && (
                          <div className="absolute right-2 top-9 z-30 bg-white border border-slate-200 rounded-md shadow-lg min-w-[180px] py-1" onClick={(e) => e.stopPropagation()} data-testid={`menu-${it.id}`}>
                            <button onClick={() => { setOpenMenuId(null); setEditing(it); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2" data-testid={`menu-edit-${it.id}`}>
                              <Edit3 className="w-3.5 h-3.5 text-slate-500" /> Ver y editar
                            </button>
                            <button onClick={() => { setOpenMenuId(null); handleClone(it); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2" data-testid={`menu-clone-${it.id}`}>
                              <Copy className="w-3.5 h-3.5 text-slate-500" /> Clonar
                            </button>
                            <button onClick={() => { setOpenMenuId(null); setRenamingId(it.id); setRenameValue(it.name); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2" data-testid={`menu-rename-${it.id}`}>
                              <TypeIcon className="w-3.5 h-3.5 text-slate-500" /> Renombrar
                            </button>
                            <button onClick={() => { setOpenMenuId(null); handleExport(it); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2" data-testid={`menu-export-${it.id}`}>
                              <Download className="w-3.5 h-3.5 text-slate-500" /> Exportar a Excel
                            </button>
                            <div className="border-t my-1" />
                            <button onClick={() => { setOpenMenuId(null); handleDelete(it); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-rose-50 text-rose-600 flex items-center gap-2" data-testid={`menu-delete-${it.id}`}>
                              <Trash2 className="w-3.5 h-3.5" /> Eliminar
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showCreate && (
        <CreateInformeDialog
          onClose={() => setShowCreate(false)}
          onCreated={(it) => { setShowCreate(false); setEditing(it); fetchItems(); }}
        />
      )}

      {editing && (
        <InformeBuilderDialog
          informe={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchItems(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// CreateInformeDialog: selector de plantillas o "crear desde cero"
// ============================================================
function CreateInformeDialog({ onClose, onCreated }) {
  const { getAuthHeaders } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [pipelineId, setPipelineId] = useState("");
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/pipelines/informes/templates`, getAuthHeaders())
      .then((r) => r.json()).then((d) => setTemplates(d.items || [])).catch(() => {});
    fetch(`${API_URL}/api/pipelines`, getAuthHeaders())
      .then((r) => r.json()).then((d) => setPipelines(d.items || [])).catch(() => {});
  }, [getAuthHeaders]);

  const handleCreate = async () => {
    if (!pipelineId) { toast.error("Seleccioná un pipeline"); return; }
    if (!name.trim()) { toast.error("Ingresá un nombre"); return; }
    setCreating(true);
    try {
      const payload = {
        name: name.trim(),
        description: selectedTemplate?.description || "",
        pipeline_id: pipelineId,
        properties: selectedTemplate?.properties || ["title", "stage_name", "owner_name", "value", "expected_close_date"],
        filters: selectedTemplate?.filters || {},
        tags: selectedTemplate?.tags || [],
      };
      const r = await fetch(`${API_URL}/api/pipelines/informes`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error");
      toast.success("Informe creado");
      onCreated(d.informe);
    } catch (e) { toast.error(e.message || "Error al crear"); }
    setCreating(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="create-informe-dialog">
        <DialogHeader>
          <DialogTitle>Crear informe</DialogTitle>
        </DialogHeader>

        {/* Plantillas */}
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-2 block">Empezá con una plantilla</Label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => { setSelectedTemplate(null); if (!name) setName("Informe personalizado"); }}
              className={`p-3 border rounded-lg text-left transition-all ${selectedTemplate === null ? "border-[#193966] bg-blue-50 ring-2 ring-[#193966]/20" : "border-slate-200 hover:border-slate-300"}`}
              data-testid="tpl-blank"
            >
              <div className="flex items-center gap-2 mb-1">
                <Plus className="w-4 h-4 text-[#193966]" />
                <span className="font-semibold text-sm">Crear desde cero</span>
              </div>
              <p className="text-[11px] text-slate-500">Elegí las propiedades y filtros que quieras</p>
            </button>
            {templates.map((t) => {
              const Icon = TEMPLATE_ICONS[t.icon] || BarChart3;
              const selected = selectedTemplate?.id === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { setSelectedTemplate(t); if (!name) setName(t.name); }}
                  className={`p-3 border rounded-lg text-left transition-all ${selected ? "border-[#193966] bg-blue-50 ring-2 ring-[#193966]/20" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`tpl-${t.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4 text-[#193966]" />
                    <span className="font-semibold text-sm">{t.name}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 line-clamp-2">{t.description}</p>
                  <div className="flex gap-1 mt-1.5">
                    {(t.tags || []).map((tag) => (
                      <Badge key={tag} variant="outline" className="text-[9px] py-0">{tag}</Badge>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Pipeline + Nombre */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-xs">Pipeline</Label>
            <select
              value={pipelineId}
              onChange={(e) => setPipelineId(e.target.value)}
              className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white"
              data-testid="create-pipeline-select"
            >
              <option value="">— Seleccioná un pipeline —</option>
              {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Nombre del informe</Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Negocios ganados en Q1"
              className="h-9"
              data-testid="create-name-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="h-9">
            <X className="w-4 h-4 mr-1" /> Cancelar
          </Button>
          <Button onClick={handleCreate} disabled={creating} className="h-9 bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="create-confirm-btn">
            {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Crear y configurar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// InformeBuilderDialog: edita propiedades, filtros, y guarda
// ============================================================
function InformeBuilderDialog({ informe, onClose, onSaved }) {
  const { getAuthHeaders } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [name, setName] = useState(informe.name || "");
  const [tagsCsv, setTagsCsv] = useState((informe.tags || []).join(", "));
  const [selected, setSelected] = useState(informe.properties || []);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  // Load full informe (with filters) and catalog
  useEffect(() => {
    fetch(`${API_URL}/api/pipelines/widget-properties/${informe.pipeline_id}`, getAuthHeaders())
      .then((r) => r.json()).then((d) => setCatalog(d.items || [])).catch(() => {});
  }, [informe.pipeline_id, getAuthHeaders]);

  // Reload preview whenever selected props change (uses saved filters in informe)
  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/pipelines/informes/${informe.id}/data?limit=100`, getAuthHeaders())
      .then((r) => r.json()).then((d) => {
        // Sync selected props with informe ones the first time
        setPreviewData(d);
        setLoading(false);
      }).catch(() => setLoading(false));
  }, [informe.id, getAuthHeaders, selected.length]);

  const labelByKey = useMemo(() => {
    const m = {};
    catalog.forEach((p) => { m[p.key] = p.label; });
    return m;
  }, [catalog]);

  const catalogByCategory = useMemo(() => {
    const grouped = {};
    catalog.forEach((p) => {
      if (search && !(p.label.toLowerCase().includes(search.toLowerCase()) || p.key.toLowerCase().includes(search.toLowerCase()))) return;
      (grouped[p.category] = grouped[p.category] || []).push(p);
    });
    return grouped;
  }, [catalog, search]);

  const toggleProp = (key) => {
    setSelected((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const moveProp = (from, to) => {
    if (from === to || to < 0 || to >= selected.length) return;
    setSelected((prev) => { const n = [...prev]; const [x] = n.splice(from, 1); n.splice(to, 0, x); return n; });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: name.trim() || informe.name,
        description: informe.description || "",
        pipeline_id: informe.pipeline_id,
        properties: selected,
        filters: informe.filters || {},
        tags: tagsCsv.split(",").map((t) => t.trim()).filter(Boolean),
      };
      const r = await fetch(`${API_URL}/api/pipelines/informes/${informe.id}`, {
        method: "PUT",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error();
      toast.success("Informe guardado");
      onSaved();
    } catch { toast.error("Error al guardar"); }
    setSaving(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/informes/${informe.id}/export`, getAuthHeaders());
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(informe.name || "informe").replace(/\s+/g, "_")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exportado a Excel");
    } catch { toast.error("Error al exportar"); }
    setExporting(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] overflow-hidden flex flex-col p-0" data-testid="informe-builder-dialog">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#193966] to-[#80BC00] text-white flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-8 text-base font-bold border-0 px-1 focus-visible:ring-1"
                data-testid="builder-name"
              />
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="text"
                  value={tagsCsv}
                  onChange={(e) => setTagsCsv(e.target.value)}
                  className="h-6 text-[11px] border-0 px-1 flex-1"
                  placeholder="Etiquetas separadas por coma (ej. Ventas, Q1)"
                  data-testid="builder-tags"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={handleExport} disabled={exporting} className="h-9" data-testid="builder-export">
              {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
              Exportar a Excel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="h-9 bg-[#193966] hover:bg-[#152d52] text-white" data-testid="builder-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Guardar
            </Button>
            <Button size="sm" variant="outline" onClick={onClose} className="h-9">
              <X className="w-4 h-4 mr-1" /> Cerrar
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 flex overflow-hidden">
          <aside className="w-[340px] border-r border-slate-200 bg-slate-50 flex flex-col overflow-hidden">
            <div className="p-3 border-b border-slate-200 bg-white">
              <h3 className="text-sm font-bold text-slate-800">Propiedades</h3>
              <p className="text-[11px] text-slate-500">Agregá las columnas que querés en este informe.</p>
            </div>
            <div className="p-3 border-b border-slate-200 bg-white">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Seleccionadas ({selected.length})</p>
              {selected.length === 0 ? (
                <p className="text-[11px] text-slate-400 italic">Marcá del catálogo abajo.</p>
              ) : (
                <div className="space-y-1 max-h-[35vh] overflow-y-auto">
                  {selected.map((key, idx) => (
                    <div key={key} className="flex items-center gap-1.5 bg-white border border-slate-200 rounded px-2 py-1.5 group hover:border-[#193966]" data-testid={`builder-sel-${key}`}>
                      <button onClick={() => moveProp(idx, idx - 1)} disabled={idx === 0} className="text-slate-300 hover:text-slate-600 disabled:opacity-30"><span className="text-xs">▲</span></button>
                      <button onClick={() => moveProp(idx, idx + 1)} disabled={idx === selected.length - 1} className="text-slate-300 hover:text-slate-600 disabled:opacity-30"><span className="text-xs">▼</span></button>
                      <span className="text-xs text-slate-700 flex-1 truncate">{labelByKey[key] || key}</span>
                      <button onClick={() => toggleProp(key)} className="text-slate-400 hover:text-rose-500 opacity-0 group-hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <div className="relative mb-2">
                <SearchIcon className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                <Input type="text" placeholder="Buscar propiedad…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 text-xs pl-7" />
              </div>
              {Object.entries(catalogByCategory).map(([cat, items]) => (
                <div key={cat} className="mb-3">
                  <p className="text-[10px] uppercase tracking-wider text-indigo-600 font-bold mb-1">{cat}</p>
                  <div className="space-y-0.5">
                    {items.map((p) => (
                      <label key={p.key} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-white rounded px-1.5 py-1">
                        <input type="checkbox" checked={selected.includes(p.key)} onChange={() => toggleProp(p.key)} className="w-3.5 h-3.5 accent-[#193966]" />
                        <span className="truncate text-slate-700">{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </aside>

          <main className="flex-1 flex flex-col overflow-hidden bg-white">
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <p className="text-xs text-slate-600">
                {loading ? "Cargando…" : previewData ? (
                  <>Mostrando <b>{previewData.shown}</b> de <b>{previewData.total}</b> tarjetas · {selected.length} columnas</>
                ) : "—"}
              </p>
              <p className="text-[11px] text-slate-400 flex items-center gap-1"><Eye className="w-3 h-3" /> Vista previa con filtros guardados del informe</p>
            </div>
            {loading ? (
              <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
            ) : !previewData?.rows?.length ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mb-2">
                  <Database className="w-6 h-6 text-slate-400" />
                </div>
                <p className="text-sm font-semibold text-slate-700">No hay tarjetas para mostrar</p>
                <p className="text-[11px] text-slate-500 mt-1">Ajustá las propiedades o filtros del informe.</p>
              </div>
            ) : (
              <div className="flex-1 overflow-auto">
                <table className="w-full text-[12px]">
                  <thead className="bg-slate-100 border-b sticky top-0 z-10">
                    <tr>
                      {selected.map((k) => (
                        <th key={k} className="text-left px-3 py-2 font-bold uppercase text-[10px] tracking-wider text-slate-600 whitespace-nowrap">{labelByKey[k] || k}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.rows.map((row) => (
                      <tr key={row.id} className="border-b hover:bg-slate-50">
                        {selected.map((k) => (
                          <td key={k} className="px-3 py-2 whitespace-nowrap max-w-[260px] truncate text-slate-700">
                            {row[k] === null || row[k] === undefined ? "—" : (Array.isArray(row[k]) ? row[k].join(", ") : String(row[k]))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
}
